import {
  Component,
  Inject,
  ViewChild,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import {
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import {
  GroupCalendarEventCell,
  GroupCalendarTemplateOption,
} from './group-calendar-settings.interface';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form.component';
import {
  buildExistingEventCellFormPatch,
  buildSingleEventFormDefaults,
} from '../../../services/component-data-dest/data/add-event';
import templateSingleEventForm from '../duna-template-to-events/template-single-event-form.json';
import { environment } from '../../../environment/environment';
import { EventDTO } from '../../../dto/event/event.dto';

/** Shared MatDialog size for template → create event (dual-panel modules need width). */
export const DAY_CELL_ACTION_DIALOG_CONFIG = {
  width: 'min(1100px, 96vw)',
  maxWidth: '96vw',
  panelClass: 'day-cell-action-dialog-panel',
};

export interface DayCellActionDialogData {
  dateLabel: string;
  locationLabel: string;
  dateKey: string;
  locationKey: string;
  existingEvent: GroupCalendarEventCell | null;
  templates: GroupCalendarTemplateOption[];
  templateColors: Record<string, string>;
  defaultTemplateId: number | null;
  /** Groups assigned to the day; user picks one when creating a new event. */
  dayGroupOptions?: { group_id: number; name: string }[];
}

@Component({
  selector: 'app-day-cell-action-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
    DynamicFormComponent,
  ],
  templateUrl: './day-cell-action-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './day-cell-action-dialog.component.scss',
})
export class DayCellActionDialogComponent implements OnInit {
  @ViewChild('eventForm') eventForm?: DynamicFormComponent;

  selectedTemplateId: number | null;
  selectedGroupId: number | null = null;
  eventFormData: Record<string, unknown> = {};
  /** Blocks form until existing event modules/details are loaded for dual-panel. */
  loadingExistingEventPatch = false;
  private existingEventFormPatch: Record<string, unknown> | null = null;

  readonly eventFormSettings = {
    hideSubmitButton: true,
    showEmptyFormSubmit: false,
    showNavigationButtons: false,
    // 'none' stops forceUpdateNavigationButtons from trying to restore the nav bar
    // (which causes a display:flex !important flash every ~400 ms due to the keep-nav
    // interval fighting against SurveyJS's own showNavigationButtons:false rendering).
    navigationButtonsLocation: 'none',
  };

  constructor(
    private dialogRef: MatDialogRef<DayCellActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DayCellActionDialogData,
    private http: HttpClient
  ) {
    const existing = data.existingEvent;
    const eventTemplateId = existing?.template_id ?? null;
    if (
      eventTemplateId != null &&
      data.templates.some((t) => t.template_id === eventTemplateId)
    ) {
      this.selectedTemplateId = eventTemplateId;
    } else {
      this.selectedTemplateId = data.defaultTemplateId;
    }

    this.loadingExistingEventPatch = !!existing?.guid;
    const dayGroups = data.dayGroupOptions ?? [];
    if (existing?.user_group_id != null) {
      this.selectedGroupId = existing.user_group_id;
    } else if (dayGroups.length === 1) {
      this.selectedGroupId = dayGroups[0].group_id;
    }
    if (!existing?.guid && this.selectedTemplateId != null) {
      this.refreshEventFormData();
    }
  }

  get showGroupPicker(): boolean {
    return (
      !this.data.existingEvent &&
      (this.data.dayGroupOptions?.length ?? 0) > 1
    );
  }

  get canConfirmCreate(): boolean {
    if (this.selectedTemplateId == null) return false;
    if (this.showGroupPicker && this.selectedGroupId == null) return false;
    return true;
  }

  ngOnInit(): void {
    const guid = this.data.existingEvent?.guid;
    if (!guid) return;
    void this.loadExistingEventFormPatch(guid);
  }

  private async loadExistingEventFormPatch(guid: string): Promise<void> {
    try {
      const event = await firstValueFrom(
        this.http.get<EventDTO>(`${environment.apiUrl}/events/${guid}`)
      );
      this.existingEventFormPatch = buildExistingEventCellFormPatch(event);
    } catch (error) {
      console.warn('[DayCellActionDialog] Could not load existing event:', error);
      this.existingEventFormPatch = null;
    } finally {
      this.loadingExistingEventPatch = false;
      if (this.selectedTemplateId != null) {
        this.refreshEventFormData();
      }
    }
  }

  get selectedTemplate(): GroupCalendarTemplateOption | undefined {
    if (this.selectedTemplateId == null) return undefined;
    return this.data.templates.find(
      (t) => t.template_id === this.selectedTemplateId
    );
  }

  getColor(templateId: number): string {
    return this.data.templateColors[String(templateId)] || '#94a3b8';
  }

  getEventFormConfig(): typeof templateSingleEventForm {
    return templateSingleEventForm;
  }

  selectTemplate(templateId: number): void {
    this.selectedTemplateId = templateId;
    this.refreshEventFormData();

    // If the SurveyJS model already exists (form alive from a previous selection), update only
    // the fields that actually changed. This avoids firing onValueChanged for sub_event_location
    // (which never changes between templates in the same calendar cell), preventing the
    // onChangeUpdateQuestion → locations_rooms API reload that caused the 1-2 s delay.
    // The form component itself is never destroyed/recreated on template switches because
    // *ngIf="selectedTemplate" stays truthy the whole time — no formRenderKey needed.
    this.applyEventFormDataToSurveyModel();
    // When existingModel is absent the form is being created for the first time by *ngIf
    // becoming truthy; it will initialize itself from the modalData binding.
  }

  private refreshEventFormData(): void {
    const template = this.selectedTemplate;
    if (!template) {
      this.eventFormData = {};
      return;
    }
    const date = new Date(this.data.dateKey);
    this.eventFormData = {
      ...buildSingleEventFormDefaults(template.template_data, {
        date,
        locationKey: this.data.locationKey,
      }),
      ...(this.existingEventFormPatch ?? {}),
    };
  }

  private applyEventFormDataToSurveyModel(): void {
    const existingModel = this.eventForm?.surveyModel;
    if (!existingModel) return;
    const current = (existingModel.data ?? {}) as Record<string, unknown>;
    for (const [key, value] of Object.entries(this.eventFormData)) {
      const changed =
        key === 'event_modules'
          ? JSON.stringify(current[key]) !== JSON.stringify(value)
          : current[key] !== value;
      if (changed) {
        existingModel.setValue(key, value);
      }
    }
  }

  private getEventFormValues(): Record<string, unknown> {
    return ((this.eventForm?.surveyModel as { data?: Record<string, unknown> })
      ?.data || this.eventFormData) as Record<string, unknown>;
  }

  private validateEventForm(): boolean {
    const model = this.eventForm?.surveyModel;
    if (!model) return false;
    return model.validate();
  }

  confirm(): void {
    if (this.selectedTemplateId == null) return;
    const template = this.selectedTemplate;
    if (!template) return;
    if (!this.validateEventForm()) return;
    this.dialogRef.close({
      action: 'create',
      template,
      formValues: { ...this.getEventFormValues() },
      groupId: this.selectedGroupId,
    });
  }

  replace(): void {
    this.confirm();
  }

  remove(): void {
    this.dialogRef.close({ action: 'remove' });
  }

  getEventUrl(): string | null {
    const guid = this.data.existingEvent?.guid;
    if (!guid) return null;
    return `${window.location.origin}/edit-event/${guid}`;
  }

  openInNewTab(): void {
    const url = this.getEventUrl();
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
